import type { CatalogSection } from './catalogLoadPolicy';

export type CatalogQueryValue =
  | boolean
  | number
  | string
  | null
  | readonly CatalogQueryValue[]
  | { readonly [key: string]: CatalogQueryValue | undefined };

export type CatalogAuthorizationIdentity = {
  companyId: string;
  actorUserId: string;
  permissionVersion: string;
  roleViewId?: string;
  roleViewPolicyVersion?: string;
  permissionDataScopes?: Record<string, readonly CatalogQueryValue[]>;
  permissions?: readonly string[];
  dataScopes?: readonly CatalogQueryValue[];
  departments?: readonly string[];
  primaryDepartmentId?: string;
};

export type CatalogResourceKey = {
  scope: string;
  apiIdentity: object;
  section: CatalogSection;
  query: Readonly<Record<string, CatalogQueryValue | undefined>>;
};

export type CatalogResourceStatus = 'missing' | 'loading' | 'ready';

type Entry = {
  key: CatalogResourceKey;
  scope: string;
  section: CatalogSection;
  fingerprint: string;
  controller: AbortController;
  status: Exclude<CatalogResourceStatus, 'missing'>;
  promise: Promise<unknown>;
  value?: unknown;
  resolvedAt?: number;
  maxAgeMs?: number;
  expiresAt?: number;
};

export type CatalogResourceEvent = {
  scope: string | null;
  sections: readonly CatalogSection[];
  reason: 'status' | 'mutation' | 'clear' | 'partial' | 'authorization-changed';
  value?: unknown;
};

const SIGNED_PREVIEW_EXPIRY_SAFETY_MS = 30_000;

/** Returns the earliest usable signed-preview deadline found in a DTO graph. */
export function catalogPreviewCacheExpiresAt(
  value: unknown,
): number | undefined {
  let earliest = Number.POSITIVE_INFINITY;
  const visited = new WeakSet<object>();
  const visit = (candidate: unknown, depth: number) => {
    if (depth > 16 || candidate === null || typeof candidate !== 'object')
      return;
    if (visited.has(candidate)) return;
    visited.add(candidate);
    if (Array.isArray(candidate)) {
      for (const child of candidate) visit(child, depth + 1);
      return;
    }
    for (const [key, child] of Object.entries(candidate)) {
      if (
        (key === 'previewExpiresAt' || key === 'thumbnailExpiresAt') &&
        typeof child === 'string'
      ) {
        const parsed = Date.parse(child);
        if (Number.isFinite(parsed)) earliest = Math.min(earliest, parsed);
      } else {
        visit(child, depth + 1);
      }
    }
  };
  visit(value, 0);
  return Number.isFinite(earliest)
    ? earliest - SIGNED_PREVIEW_EXPIRY_SAFETY_MS
    : undefined;
}

function normalize(value: CatalogQueryValue): CatalogQueryValue {
  if (Array.isArray(value)) return value.map(normalize);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .filter((entry) => entry[1] !== undefined)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, child]) => [key, normalize(child as CatalogQueryValue)]),
    );
  }
  if (typeof value === 'number' && !Number.isFinite(value))
    throw new Error('catalog resource keys require finite numbers');
  return value;
}

function stableKey(value: CatalogQueryValue): string {
  return JSON.stringify(normalize(value));
}

/**
 * Builds the authorization half of a catalog resource key. It deliberately
 * includes the current data scopes instead of treating company membership as
 * sufficient permission to reuse another actor's directory response.
 */
export function createCatalogSessionScopeKey(
  identity: CatalogAuthorizationIdentity,
): string {
  const permissions = identity.permissions ?? [];
  const dataScopes = identity.dataScopes ?? [];
  return stableKey({
    actorUserId: identity.actorUserId,
    companyId: identity.companyId,
    authorizationShape:
      identity.permissions && identity.dataScopes ? 'complete' : 'incomplete',
    dataScopes: [...dataScopes].map((scope) => stableKey(scope)).sort(),
    departments: [...(identity.departments ?? [])].sort(),
    primaryDepartmentId: identity.primaryDepartmentId ?? '',
    permissions: [...new Set(permissions)].sort(),
    permissionVersion: identity.permissionVersion,
    roleViewId: identity.roleViewId ?? '',
    ...(identity.roleViewPolicyVersion
      ? { roleViewPolicyVersion: identity.roleViewPolicyVersion }
      : {}),
    permissionDataScopes: identity.permissionDataScopes ?? null,
  });
}

function consumerAbortError(): Error {
  const error = new Error('Catalog resource consumer aborted');
  error.name = 'AbortError';
  return error;
}

function forConsumer<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(consumerAbortError());
  return new Promise<T>((resolve, reject) => {
    const abort = () => {
      cleanup();
      reject(consumerAbortError());
    };
    const cleanup = () => signal.removeEventListener('abort', abort);
    signal.addEventListener('abort', abort, { once: true });
    promise.then(
      (value) => {
        cleanup();
        resolve(value);
      },
      (cause) => {
        cleanup();
        reject(cause);
      },
    );
  });
}

/**
 * In-memory, authorization-scoped catalog resources for the current browser
 * session. A loader must resolve only after all pages/details required for its
 * section are complete. Nothing is written to browser storage.
 */
export class CatalogSessionResource {
  private readonly entries = new Map<string, Entry>();
  private readonly apiIds = new WeakMap<object, number>();
  private nextApiId = 1;
  private activeScope: string | null = null;
  private readonly listeners = new Set<(event: CatalogResourceEvent) => void>();
  private readonly dirtySections = new Map<CatalogSection, number>();
  private readonly sectionRevisions = new Map<CatalogSection, number>();
  private readonly refreshes = new Map<
    string,
    { controller: AbortController; promise: Promise<unknown> }
  >();

  subscribe(listener: (event: CatalogResourceEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  currentScope(): string | null {
    return this.activeScope;
  }

  /** Partial progress is observable but never stored in the complete cache. */
  publishPartial(scope: string, section: CatalogSection, value: unknown): void {
    if (this.activeScope === scope)
      this.emit({ scope, sections: [section], reason: 'partial', value });
  }

  revokeSection(scope: string, section: CatalogSection): void {
    if (this.activeScope !== scope) return;
    this.invalidate(scope, [section]);
    const revision = this.revision(scope, section) + 1;
    this.sectionRevisions.set(section, revision);
    this.dirtySections.set(section, revision);
    this.emit({ scope, sections: [section], reason: 'authorization-changed' });
  }

  revision(scope: string, section: CatalogSection): number {
    return this.activeScope === scope
      ? (this.sectionRevisions.get(section) ?? 0)
      : 0;
  }

  needsRefresh(scope: string, section: CatalogSection): boolean {
    return this.activeScope === scope && this.dirtySections.has(section);
  }

  evictDetails(
    scope: string,
    section: CatalogSection,
    ids: readonly string[],
  ): void {
    if (this.activeScope !== scope) return;
    const selected = new Set(ids);
    for (const entry of [...this.entries.values()])
      if (
        entry.section === section &&
        entry.key.query.detail &&
        selected.has(String(entry.key.query.id))
      )
        this.remove(entry.fingerprint);
  }

  markRefreshed(
    scope: string,
    section: CatalogSection,
    revision: number,
  ): void {
    if (
      this.activeScope === scope &&
      this.revision(scope, section) === revision
    )
      this.dirtySections.delete(section);
  }

  /** Keep the last complete directory readable; only the affected details and
   * management pages are discarded. The next index probe determines dependencies. */
  markDirty(
    scope: string,
    sections: readonly CatalogSection[],
    ids?: readonly string[],
  ): void {
    if (this.activeScope !== scope) return;
    const selected = new Set(sections);
    const selectedIds = ids ? new Set(ids) : null;
    for (const section of selected) {
      const revision = (this.sectionRevisions.get(section) ?? 0) + 1;
      this.sectionRevisions.set(section, revision);
      this.dirtySections.set(section, revision);
    }
    for (const entry of [...this.entries.values()]) {
      if (!selected.has(entry.section)) continue;
      if (
        entry.key.query.management ||
        (entry.key.query.detail &&
          (!selectedIds || selectedIds.has(String(entry.key.query.id))))
      )
        this.remove(entry.fingerprint);
    }
    this.emit({ scope, sections, reason: 'mutation' });
  }

  seed<T>(key: CatalogResourceKey, value: T, notify = true): void {
    this.activateScope(key.scope);
    const fingerprint = this.fingerprint(key);
    this.remove(fingerprint);
    this.entries.set(fingerprint, {
      key,
      scope: key.scope,
      section: key.section,
      fingerprint,
      controller: new AbortController(),
      status: 'ready',
      promise: Promise.resolve(value),
      value,
      resolvedAt: Date.now(),
    });
    if (notify)
      this.emit({
        scope: key.scope,
        sections: [key.section],
        reason: 'status',
      });
  }

  /** Revalidation has its own shared request and does not erase usable data. */
  refresh<T>(
    key: CatalogResourceKey,
    load: (signal: AbortSignal) => Promise<T>,
    consumerSignal?: AbortSignal,
    validateCommit?: (value: T) => void,
  ): Promise<T> {
    if (consumerSignal?.aborted) return Promise.reject(consumerAbortError());
    this.activateScope(key.scope);
    const fingerprint = this.fingerprint(key);
    const existing = this.refreshes.get(fingerprint);
    if (existing)
      return forConsumer(existing.promise as Promise<T>, consumerSignal);
    const entry = this.entries.get(fingerprint);
    if (entry?.status === 'loading')
      return forConsumer(entry.promise as Promise<T>, consumerSignal);
    const controller = new AbortController();
    const refresh = {
      controller,
      promise: Promise.resolve() as Promise<unknown>,
    };
    refresh.promise = Promise.resolve()
      .then(() => load(controller.signal))
      .then((value) => {
        if (controller.signal.aborted || this.activeScope !== key.scope)
          throw consumerAbortError();
        // Loader completion and this Promise reaction are different microtasks.
        // Recheck its verified epoch atomically with the actual cache commit.
        validateCommit?.(value);
        this.seed(key, value);
        return value;
      })
      .finally(() => {
        if (this.refreshes.get(fingerprint) === refresh)
          this.refreshes.delete(fingerprint);
      });
    this.refreshes.set(fingerprint, refresh);
    return forConsumer(refresh.promise as Promise<T>, consumerSignal);
  }

  read<T>(
    key: CatalogResourceKey,
    load: (resourceSignal: AbortSignal) => Promise<T>,
    options: {
      consumerSignal?: AbortSignal;
      force?: boolean;
      maxAgeMs?: number;
      expiresAt?: (value: T) => number | undefined;
    } = {},
  ): Promise<T> {
    if (options.consumerSignal?.aborted)
      return Promise.reject(consumerAbortError());
    this.activateScope(key.scope);
    const fingerprint = this.fingerprint(key);
    if (options.force) this.remove(fingerprint);
    const existing = this.freshEntry(fingerprint);
    if (existing)
      return forConsumer(
        existing.promise as Promise<T>,
        options.consumerSignal,
      );

    const controller = new AbortController();
    const entry: Entry = {
      key,
      scope: key.scope,
      section: key.section,
      fingerprint,
      controller,
      status: 'loading',
      promise: Promise.resolve(),
      maxAgeMs: options.maxAgeMs,
    };
    let loading: Promise<T>;
    try {
      loading = load(controller.signal);
    } catch (cause) {
      loading = Promise.reject(cause);
    }
    entry.promise = loading.then(
      (value) => {
        if (controller.signal.aborted) throw consumerAbortError();
        entry.status = 'ready';
        entry.value = value;
        entry.resolvedAt = Date.now();
        entry.expiresAt = options.expiresAt?.(value);
        this.emit({
          scope: key.scope,
          sections: [key.section],
          reason: 'status',
        });
        return value;
      },
      (cause) => {
        if (this.entries.get(fingerprint) === entry) {
          this.entries.delete(fingerprint);
          this.emit({
            scope: key.scope,
            sections: [key.section],
            reason: 'status',
          });
        }
        throw cause;
      },
    );
    this.entries.set(fingerprint, entry);
    this.emit({ scope: key.scope, sections: [key.section], reason: 'status' });
    return forConsumer(entry.promise as Promise<T>, options.consumerSignal);
  }

  retry<T>(
    key: CatalogResourceKey,
    load: (resourceSignal: AbortSignal) => Promise<T>,
    consumerSignal?: AbortSignal,
  ): Promise<T> {
    return this.read(key, load, { consumerSignal, force: true });
  }

  status(key: CatalogResourceKey): CatalogResourceStatus {
    if (this.activeScope !== key.scope) return 'missing';
    return this.freshEntry(this.fingerprint(key))?.status ?? 'missing';
  }

  value<T>(key: CatalogResourceKey): T | undefined {
    if (this.activeScope !== key.scope) return undefined;
    const entry = this.freshEntry(this.fingerprint(key));
    return entry?.status === 'ready' ? (entry.value as T) : undefined;
  }

  /** Invalidates selected complete sections after a catalog write. */
  invalidate(scope: string, sections?: readonly CatalogSection[]): void {
    if (this.activeScope !== scope) return;
    const selected = sections ? new Set(sections) : null;
    for (const entry of [...this.entries.values()]) {
      if (!selected || selected.has(entry.section))
        this.remove(entry.fingerprint);
    }
  }

  invalidateActive(sections?: readonly CatalogSection[]): void {
    if (this.activeScope) this.invalidate(this.activeScope, sections);
  }

  clear(): void {
    for (const entry of this.entries.values()) entry.controller.abort();
    this.entries.clear();
    for (const refresh of this.refreshes.values()) refresh.controller.abort();
    this.refreshes.clear();
    this.dirtySections.clear();
    this.sectionRevisions.clear();
    this.activeScope = null;
    this.emit({ scope: null, sections: [], reason: 'clear' });
  }

  private activateScope(scope: string): void {
    if (this.activeScope === scope) return;
    this.clear();
    this.activeScope = scope;
  }

  private fingerprint(key: CatalogResourceKey): string {
    let apiId = this.apiIds.get(key.apiIdentity);
    if (!apiId) {
      apiId = this.nextApiId;
      this.nextApiId += 1;
      this.apiIds.set(key.apiIdentity, apiId);
    }
    return `${key.scope}\u0000${apiId}\u0000${key.section}\u0000${stableKey(
      key.query,
    )}`;
  }

  private remove(fingerprint: string): void {
    const entry = this.entries.get(fingerprint);
    if (!entry) return;
    this.entries.delete(fingerprint);
    entry.controller.abort();
  }

  private freshEntry(fingerprint: string): Entry | undefined {
    const entry = this.entries.get(fingerprint);
    if (
      entry?.status === 'ready' &&
      ((entry.maxAgeMs !== undefined &&
        entry.resolvedAt !== undefined &&
        Date.now() - entry.resolvedAt >= entry.maxAgeMs) ||
        (entry.expiresAt !== undefined && Date.now() >= entry.expiresAt))
    ) {
      this.remove(fingerprint);
      return undefined;
    }
    return entry;
  }

  private emit(event: CatalogResourceEvent): void {
    for (const listener of this.listeners) listener(event);
  }
}

/** Shared by quote workspace instances; its lifetime is the loaded app module. */
export const quoteCatalogSessionResource = new CatalogSessionResource();

export function invalidateQuoteCatalogSession(
  sections?: readonly CatalogSection[],
): void {
  quoteCatalogSessionResource.invalidateActive(sections);
}

/** Capture scope before the write so a late response cannot dirty another role. */
export function notifyQuoteCatalogMutation(
  scope: string | null,
  section: CatalogSection,
  ids: readonly string[],
  dependencies: readonly CatalogSection[] = [],
): void {
  if (!scope) return;
  quoteCatalogSessionResource.markDirty(scope, [section], ids);
  if (dependencies.length)
    quoteCatalogSessionResource.markDirty(scope, dependencies, []);
}
